import { Box, Button, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import EditIcon from '@mui/icons-material/Edit';
import {
  DateInput,
  Edit,
  ImageField,
  ImageInput,
  Loading,
  required,
  SimpleForm,
  TextInput,
  TranslatableInputs,
  useDataProvider,
  useNotify,
  useLocaleState,
  useGetList,
  useRefresh,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import { Link } from 'react-router-dom';
import { CustomToolbar } from '../../components/customtoolbar/customToolbar.component';
import { locales } from '../../i18n/languges';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';

const EditBlocksButton = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <Button
      component={Link}
      to={`/destinationpages/${record.id}/blocks`}
      startIcon={<EditIcon />}
    >
      {translate('resources.destinationpages.editBlocks')}
    </Button>
  );
};

const schema = yup.object().shape({
  title: yup
    .object()
    .shape({
      en: yup.string().required(),
    })
    .required(),
});

const resolver = yupResolver<FieldValues>(schema);

const toLocalizedText = (value: any, locale: string): string => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    return value[locale] || value.en || value.sv || '';
  }

  return '';
};

const truncate = (value: string, maxLen = 90): string => {
  if (value.length <= maxLen) {
    return value;
  }
  return `${value.slice(0, maxLen - 1)}...`;
};

const sortByPriority = (rows: any[]) => {
  return [...rows].sort((a: any, b: any) => {
    const pa = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : Number.MAX_SAFE_INTEGER;
    const pb = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : Number.MAX_SAFE_INTEGER;

    if (pa !== pb) {
      return pa - pb;
    }

    return Number(a?.id ?? Number.MAX_SAFE_INTEGER) - Number(b?.id ?? Number.MAX_SAFE_INTEGER);
  });
};

const moveItem = (rows: any[], from: number, to: number) => {
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const getPreviewHeight = (mediaHeight?: number): number => {
  if (!mediaHeight) {
    return 90;
  }
  return Math.max(50, Math.min(220, Math.round(mediaHeight * 0.4)));
};

const getPreviewWidth = () => 360;

const MobileDestinationPreview = ({ rowsOverride }: { rowsOverride?: any[] }) => {
  const record = useRecordContext();
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const liveTitle = useWatch({ name: 'title' });
  const liveMedia = useWatch({ name: 'media' });

  const blocks = useGetList('destinationpages/blocks', {
    filter: { pageId: record?.id },
    sort: { field: 'priority', order: 'ASC' },
    pagination: { page: 1, perPage: 100 },
  });

  if (!record?.id || blocks.isLoading) {
    return null;
  }

  const rows = rowsOverride ?? blocks.data ?? [];
  const width = getPreviewWidth();
  const heroHeight = Math.round(width * 0.52);
  const previewMedia = Array.isArray(liveMedia) ? liveMedia[0] : liveMedia;
  const heroImage = previewMedia?.src || previewMedia?.fullUrl || record?.media?.fullUrl;

  return (
    <Box sx={{ position: 'sticky', top: 24, alignSelf: 'flex-start' }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {translate('resources.destinationpages.structure.previewPhoneTitle')}
      </Typography>

      <Box
        sx={{
          width,
          maxWidth: '100%',
          borderRadius: 6,
          border: '1px solid #d4d9df',
          backgroundColor: '#ffffff',
          boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            height: heroHeight,
            background: 'linear-gradient(135deg, #7c8f9f, #d7e0e8)',
            backgroundImage: heroImage ? `url(${heroImage})` : 'linear-gradient(135deg, #7c8f9f, #d7e0e8)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            p: 2,
          }}
        >
          <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(5,16,26,0.12), rgba(5,16,26,0.54))' }} />
          <Typography variant="h6" sx={{ position: 'relative', color: '#fff', fontWeight: 700, lineHeight: 1.1 }}>
            {toLocalizedText(liveTitle ?? record?.title ?? {}, locale) || translate('resources.destinationpages.previewFallbackTitle')}
          </Typography>
        </Box>

        <Box sx={{ p: 2, backgroundColor: '#f3f5f7' }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 700 }}>
            {translate('resources.destinationpages.structure.previewTitle')}
          </Typography>

          <Stack spacing={1.5}>
            {rows.map((block: any) => {
              const contentPreview = truncate(toLocalizedText(block.content, locale));
              const captionPreview = truncate(toLocalizedText(block.caption, locale));
              const isMediaBlock = block.blockType === 'image' || block.blockType === 'video';
              const previewHeight = getPreviewHeight(block.mediaHeight);

              return (
                <Box
                  key={`mobile-preview-${block.id}`}
                  sx={{
                    backgroundColor: '#f7f9fb',
                    borderRadius: 0.75,
                    p: 1.25,
                    border: '1px solid rgba(22, 34, 46, 0.12)',
                  }}
                >
                  {block.blockType === 'title' ? (
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: contentPreview ? 0.5 : 0 }}>
                      {contentPreview}
                    </Typography>
                  ) : null}

                  {block.blockType === 'body' ? (
                    <Typography variant="body2" sx={{ color: '#2f3a44', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                      {contentPreview}
                    </Typography>
                  ) : null}

                  {isMediaBlock ? (
                    <>
                      <Box
                        sx={{
                          width: '100%',
                          height: previewHeight,
                          borderRadius: 0.75,
                          overflow: 'hidden',
                          backgroundImage: block?.media?.fullUrl ? `url(${block.media.fullUrl})` : 'linear-gradient(135deg, #b8c8d9, #dce5ef)',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {block.blockType === 'video' ? (
                          <Box sx={{ width: 54, height: 54, borderRadius: '50%', backgroundColor: 'rgba(16,33,48,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ color: '#fff', ml: 0.5, fontSize: 18 }}>▶</Typography>
                          </Box>
                        ) : null}
                      </Box>
                      {captionPreview ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                          {captionPreview}
                        </Typography>
                      ) : null}
                    </>
                  ) : null}

                  {!contentPreview && !isMediaBlock ? (
                    <Typography variant="body2" color="text.secondary">
                      {translate('resources.destinationpages.structure.noContentPreview')}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

const DestinationBlocksOverview = ({ onRowsChange }: { onRowsChange?: (rows: any[]) => void }) => {
  const record = useRecordContext();
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const blocks = useGetList('destinationpages/blocks', {
    filter: { pageId: record?.id },
    sort: { field: 'priority', order: 'ASC' },
    pagination: { page: 1, perPage: 100 },
  });

  const initialRows = useMemo(() => sortByPriority(blocks.data ?? []), [blocks.data]);
  const [rows, setRows] = useState<any[]>(initialRows);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  useEffect(() => {
    onRowsChange?.(rows);
  }, [onRowsChange, rows]);

  const hasPendingOrder = useMemo(() => {
    if (rows.length !== initialRows.length) {
      return true;
    }

    return rows.some((row, index) => row?.id !== initialRows[index]?.id);
  }, [initialRows, rows]);

  if (!record?.id) {
    return null;
  }

  if (blocks.isLoading) {
    return <Loading />;
  }

  const moveRow = (from: number, to: number) => {
    if (to < 0 || to >= rows.length || from === to || saving) {
      return;
    }

    setRows(prevRows => moveItem(prevRows, from, to));
  };

  const saveOrder = async () => {
    if (!hasPendingOrder || saving) {
      return;
    }

    setSaving(true);
    try {
      await Promise.all(
        rows.map((row, index) =>
          dataProvider.update('destinationpages/blocks', {
            id: row.id,
            data: { priority: index + 1 },
            previousData: row,
          }),
        ),
      );

      notify(translate('resources.destinationpages.structure.reorderSaved', { _: 'Ordningen har sparats.' }), { type: 'info' });
      refresh();
    } catch (error) {
      notify(translate('resources.destinationpages.structure.reorderFailed', { _: 'Kunde inte spara ordningen.' }), { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 3, border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">{translate('resources.destinationpages.structure.title')}</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            component={Link}
            to={`/destinationpages/${record.id}/blocks/create`}
            startIcon={<AddIcon />}
            variant="contained"
            size="small"
          >
            {translate('resources.destinationpages.structure.addBlock')}
          </Button>
          <Button
            component={Link}
            to={`/destinationpages/${record.id}/blocks`}
            startIcon={<EditIcon />}
            size="small"
          >
            {translate('resources.destinationpages.structure.openList')}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={saveOrder}
            disabled={!hasPendingOrder || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {translate('resources.destinationpages.structure.saveOrder', { _: 'Spara ordning' })}
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('resources.destinationpages.structure.hint')}
      </Typography>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {translate('resources.destinationpages.structure.empty')}
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {rows.map((block: any, index: number) => {
            const contentPreview = truncate(toLocalizedText(block.content, locale));
            const captionPreview = truncate(toLocalizedText(block.caption, locale));

            return (
              <Box
                key={block.id}
                sx={{
                  border: '1px solid #ececec',
                  borderRadius: 1.5,
                  p: 1.25,
                  backgroundColor: '#fafafa',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                    <Chip size="small" label={`#${index + 1}`} />
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={translate(`resources.enums.destinationBlockType.${block.blockType}`)}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {`${translate('resources.destinationpages.fields.priority')}: ${block.priority ?? '-'}`}
                    </Typography>
                    {(block.blockType === 'image' || block.blockType === 'video') ? (
                      <Typography variant="caption" color="text.secondary">
                        {`${translate('resources.destinationpages.fields.mediaHeight')}: ${block.mediaHeight ?? '-'} px`}
                      </Typography>
                    ) : null}
                  </Stack>

                  <Button
                    component={Link}
                    to={`/destinationpages/blocks/${block.id}`}
                    size="small"
                    startIcon={<EditIcon />}
                  >
                    {translate('ra.action.edit')}
                  </Button>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      disabled={saving || index === 0}
                      onClick={() => moveRow(index, index - 1)}
                      aria-label={translate('resources.destinationpages.structure.moveUp', { _: 'Flytta upp' })}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={saving || index === rows.length - 1}
                      onClick={() => moveRow(index, index + 1)}
                      aria-label={translate('resources.destinationpages.structure.moveDown', { _: 'Flytta ned' })}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                {contentPreview ? (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {contentPreview}
                  </Typography>
                ) : null}

                {captionPreview ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {captionPreview}
                  </Typography>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export const DestinationPageEdit = () => {
  const [previewRows, setPreviewRows] = useState<any[] | undefined>(undefined);

  return (
    <Edit redirect="/destinationpages">
      <SimpleForm toolbar={<CustomToolbar noRedirect />} resolver={resolver}>
        <Box sx={{ display: { xs: 'block', lg: 'grid' }, gridTemplateColumns: { lg: 'minmax(0, 1fr) 420px' }, gap: 3, alignItems: 'start' }}>
          <Box>
          <TranslatableInputs fullWidth={true} locales={locales} sx={{ marginBottom: '2rem' }}>
            <TextInput source="title" label="resources.destinationpages.fields.title" validate={[required()]} />
          </TranslatableInputs>

          <ImageInput source="media" multiple={false} label="Headerbild" sx={{ mb: 2 }}>
            <ImageField source="src" title="title" />
          </ImageInput>

          <DateInput disabled source="updatedAt" sx={{ display: 'inline-flex', mr: 1 }} />

          <EditBlocksButton />
          <DestinationBlocksOverview onRowsChange={setPreviewRows} />
          </Box>

          <MobileDestinationPreview rowsOverride={previewRows} />
        </Box>
      </SimpleForm>
    </Edit>
  );
};
